import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SmoothScroll from './SmoothScroll';
import ScrollAmbient from './ScrollAmbient';
import Navigation from './Navigation';
import Footer from './Footer';
import PortfolioCarousel from './PortfolioCarousel';
import YouTubeFilmGrid from './YouTubeFilmCard';
import { TID } from '../lib/testIds';

const WEDDINGS = [
  {
    id: '5uzSqzTRa4Y',
    title: 'Aisha & Prez',
    subtitle: 'Wedding Film',
    description:
      'A heartfelt celebration of Aisha & Prez — the vows, the laughter and everyone they love, captured as a cinematic long-form wedding film.',
  },
  {
    id: 'rfEoZlTnQH8',
    title: 'Phil & Charlotte Bradford',
    subtitle: 'Wedding Film',
    description:
      "Phil & Charlotte's wedding celebration told in full — the quiet, tender moments and the big ones, woven into one romantic film.",
  },
  {
    id: '2EmNjWMed8k',
    title: 'Kellie & Christian',
    subtitle: 'Wedding Film',
    description:
      "Kellie & Christian's wedding day — a warm, cinematic celebration of two families and everyone who came to share it.",
  },
];

const DOCOS = [
  {
    id: 'HStp-4-xKp4',
    title: "Sione's Foundation",
    subtitle: 'Short Documentary',
    description:
      "Hope for Tonga — a short documentary following the people and purpose behind Sione's Foundation and the community it serves.",
  },
];

const EVENTS = [
  {
    id: 'I4Q70oTU5Ag',
    title: "Ian's 60th",
    subtitle: 'Event Film',
    description:
      "A milestone birthday to remember — Ian's 60th, capturing the speeches, the laughter and everyone who came to celebrate.",
  },
  {
    id: 'AyYGO_NeVV8',
    title: 'RNC Combine',
    subtitle: 'Event Film',
    description:
      'RNC Combine — the energy, athleticism and standout moments of the day, cut into a high-tempo event film.',
  },
];

// Older short-form clips (kept in their own "Reels" tab)
const REELS = [
  {
    type: 'video',
    src: 'https://customer-assets.emergentagent.com/job_0d17b16b-6297-4724-91a9-91fa2e7cc15b/artifacts/kaktdxsk_Tonga%20Trip%20.mp4',
    title: 'Tonga Trip',
    category: 'Travel · Film',
    client: 'Personal',
    year: '2026',
  },
  {
    type: 'video',
    src: 'https://customer-assets.emergentagent.com/job_0d17b16b-6297-4724-91a9-91fa2e7cc15b/artifacts/40yg16ia_15%20second%20vid.mp4',
    title: 'Featured Cut',
    category: 'Brand · Film',
    client: 'Studio',
    year: '2026',
  },
  {
    type: 'video',
    src: 'https://customer-assets.emergentagent.com/job_50858299-7c97-46ad-b68c-b59dc25460d7/artifacts/77gv5jik_ueta%20vid%201%20trim.mp4',
    title: 'Events',
    category: 'Live · Recap',
    client: 'Mixed',
    year: '2025',
  },
  {
    type: 'video',
    src: 'https://customer-assets.emergentagent.com/job_50858299-7c97-46ad-b68c-b59dc25460d7/artifacts/b5wnwekn_ueta%20vid%202%20trim.mp4',
    title: 'Weddings',
    category: 'Cinematic · Day',
    client: 'Private',
    year: '2025',
  },
  {
    type: 'video',
    src: 'https://customer-assets.emergentagent.com/job_50858299-7c97-46ad-b68c-b59dc25460d7/artifacts/kouyj2ey_ueta%20vid%203.mp4',
    title: 'Brand Content',
    category: 'Brand · Performance',
    client: 'Studio',
    year: '2026',
  },
];

const TABS = [
  { key: 'weddings', label: 'Weddings', count: WEDDINGS.length },
  { key: 'docos', label: 'Docos', count: DOCOS.length },
  { key: 'events', label: 'Events', count: EVENTS.length },
  { key: 'reels', label: 'Reels', count: REELS.length },
  { key: 'photography', label: 'Photography', soon: true },
];

function ComingSoon({ label, text, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative w-full overflow-hidden rounded-2xl border border-white/12 bg-[#0b0b0b] flex flex-col items-center justify-center text-center px-6"
      style={{ aspectRatio: '16 / 9' }}
    >
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D8FF3A] neon-text-glow mb-5">
        ◆ {label}
      </span>
      <h3
        className="font-display font-bold text-white tracking-tighter leading-none"
        style={{ fontSize: 'clamp(40px, 7vw, 96px)' }}
      >
        Coming soon
      </h3>
      <p className="mt-5 max-w-md text-sm md:text-base text-white/55 leading-relaxed">{text}</p>
    </div>
  );
}

export default function Portfolio() {
  const [tab, setTab] = useState('weddings');
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SmoothScroll>
      <ScrollAmbient />
      <main className="relative z-10 min-h-screen text-white">
        <Navigation />

        <section
          id="portfolio"
          data-testid={TID.portfolioRoot}
          className="relative px-6 md:px-14 pt-32 md:pt-40 pb-20 md:pb-28"
        >
          <div className="mx-auto max-w-7xl">
            <motion.button
              data-testid={TID.portfolioBackHome}
              onClick={() => navigate('/')}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="group mb-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-white transition-colors"
            >
              <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
              Back home
            </motion.button>

            <div className="flex items-center gap-4 mb-6">
              <span className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#D8FF3A] neon-text-glow">
                ◆ The portfolio
              </span>
              <span className="hidden md:inline-block h-px w-12 bg-[#D8FF3A]/60" />
            </div>

            <motion.h1
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-white font-bold tracking-tighter leading-[1.02] md:leading-[0.9] text-balance mb-6"
              style={{ fontSize: 'clamp(40px, 8vw, 120px)' }}
            >
              Selected
              <br />
              <span className="text-white/40">work.</span>
            </motion.h1>

            <p className="max-w-xl text-base md:text-lg text-white/60 leading-relaxed mb-10">
              A look through Ueta&apos;s catalogue — cinematic wedding films, documentaries and
              more. Choose a category below to explore.
            </p>

            {/* Category tabs */}
            <div className="flex flex-wrap items-center gap-3 mb-12">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  data-testid={TID.portfolioTab(t.key)}
                  onClick={() => setTab(t.key)}
                  className={`inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.25em] border transition-all ${
                    tab === t.key
                      ? 'bg-[#D8FF3A] text-black border-[#D8FF3A] neon-glow'
                      : 'bg-transparent text-white/70 border-white/20 hover:border-[#D8FF3A]/60 hover:text-white'
                  }`}
                >
                  {t.label}
                  <span className={tab === t.key ? 'text-black/60' : 'text-white/40'}>
                    {t.soon ? 'Soon' : String(t.count).padStart(2, '0')}
                  </span>
                </button>
              ))}
            </div>

            {tab === 'weddings' && (
              <YouTubeFilmGrid key="weddings" items={WEDDINGS} testidPrefix="portfolio-wedding" />
            )}

            {tab === 'docos' && (
              <YouTubeFilmGrid key="docos" items={DOCOS} testidPrefix="portfolio-doco" />
            )}

            {tab === 'reels' && <PortfolioCarousel key="reels" items={REELS} />}

            {tab === 'events' && (
              <YouTubeFilmGrid key="events" items={EVENTS} testidPrefix="portfolio-event" />
            )}

            {tab === 'photography' && (
              <ComingSoon
                label="Photography"
                testid={TID.portfolioComingSoon}
                text="Ueta's photography collection is being curated. Check back shortly."
              />
            )}
          </div>
        </section>

        <Footer />
      </main>
    </SmoothScroll>
  );
}
